import React, { useState } from 'react';
import { EventWrapperProps as RbcEventWrapperProps } from 'react-big-calendar';
import { Popover } from 'antd';
import { differenceInMinutes } from 'date-fns';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import { ICalendarEvent } from '../../../../cleaning/interfaces/CleaningPlan.types';
import { CalendarUtils } from '../../../../cleaning/utils/CalendarUtils';
import { isCleaningPlanMoreEvents } from '../../../../cleaning/utils/cappedOverlapDayLayoutAlgorithm';
import { CleaningPlanEventPopover } from '../CleaningPlanEventPopover/CleaningPlanEventPopover';
import { StyledCleaningPlanEvent, StyledCleaningPlanMoreEvents } from './CleaningPlanEvent.styles';
import { DATE_FORMAT_PATTERN } from 'lib/utils/date-handling/DateTime.types';
import { DateTime } from 'lib/utils/date-handling/DateTime';

const formatEventTimeSpan = (event: ICalendarEvent, locale: string): string => {
  if (!event.start || !event.end) {
    return '';
  }

  const start = DateTime.formatDateByLocale(locale, event.start.toISOString(), DATE_FORMAT_PATTERN.TIME_WITHOUT_SECOND);
  const end = DateTime.formatDateByLocale(locale, event.end.toISOString(), DATE_FORMAT_PATTERN.TIME_WITHOUT_SECOND);

  return `${start} - ${end}`;
};

const CleaningPlanMoreEventListItem: React.FC<{ event: ICalendarEvent; locale: string }> = ({
  event,
  locale,
}): JSX.Element => {
  const [visiblePopover, setVisiblePopover] = useState(false);
  const timeSpan = formatEventTimeSpan(event, locale);

  return (
    <li>
      <Popover
        placement="right"
        arrow={{ pointAtCenter: false }}
        content={
          <CleaningPlanEventPopover
            hidePopover={(): void => setVisiblePopover(false)}
            timeSpan={timeSpan}
            event={event}
          />
        }
        destroyTooltipOnHide={true}
        open={visiblePopover}
        onOpenChange={setVisiblePopover}
        getPopupContainer={(triggerNode): HTMLElement =>
          (document.querySelector('.rbc-time-content') as HTMLElement) ?? triggerNode
        }
      >
        <div className="cleaning-plan-more-events__item">
          <span className="cleaning-plan-more-events__name">{event.resource.cleaningPlanEvent.machine.name}</span>
          <span className="cleaning-plan-more-events__time">{timeSpan}</span>
        </div>
      </Popover>
    </li>
  );
};

const CleaningPlanMoreEvents: React.FC<RbcEventWrapperProps<ICalendarEvent>> = ({ style, event }): JSX.Element => {
  const { t, i18n } = useTranslation();
  const hiddenEvents = event.resource.hiddenEvents ?? [];
  const eventStyle = CalendarUtils.toTimeGridEventStyle(style);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover
      placement="right"
      arrow={{ pointAtCenter: false }}
      open={isOpen}
      onOpenChange={setIsOpen}
      destroyTooltipOnHide={true}
      getPopupContainer={(triggerNode): HTMLElement =>
        (document.querySelector('.rbc-time-content') as HTMLElement) ?? triggerNode
      }
      content={
        <StyledCleaningPlanMoreEvents>
          <ul className="cleaning-plan-more-events__list">
            {hiddenEvents.map((hiddenEvent, index) => (
              <CleaningPlanMoreEventListItem
                key={hiddenEvent.resource.cleaningPlanEvent.workIntervalId ?? `${hiddenEvent.title}-${index}`}
                event={hiddenEvent}
                locale={i18n.language}
              />
            ))}
          </ul>
        </StyledCleaningPlanMoreEvents>
      }
    >
      <StyledCleaningPlanEvent className="rbc-event cleaning-plan-event cleaning-plan-event--more" style={eventStyle}>
        <p className="cleaning-plan-event__event-name cleaning-plan-event__event-name--single-line">
          {t('siteDetails.cleaningPlan.moreEvents', { count: hiddenEvents.length })}
        </p>
      </StyledCleaningPlanEvent>
    </Popover>
  );
};

export const CleaningPlanEvent: React.FC<RbcEventWrapperProps<ICalendarEvent>> = (props): JSX.Element => {
  const { style, event, label } = props;
  const [visiblePopover, setVisiblePopover] = useState(false);

  if (isCleaningPlanMoreEvents(event)) {
    return <CleaningPlanMoreEvents {...props} />;
  }

  const handleHidePopover = (): void => {
    setVisiblePopover(false);
  };

  const handleVisibleChange = (newVisible: boolean): void => {
    setVisiblePopover(newVisible);
  };

  const eventStyle = CalendarUtils.toTimeGridEventStyle(style);
  const { cleaningPlanEvent } = event.resource;
  const deviceVariantName = cleaningPlanEvent.machine.type?.name;
  const eventName = deviceVariantName
    ? [cleaningPlanEvent.machine.name, deviceVariantName].join(', ')
    : cleaningPlanEvent.machine.name;

  const cleaningEventDurationMinutes = event.start && event.end ? differenceInMinutes(event.end, event.start) : 0;

  return (
    <Popover
      placement="right"
      arrow={{ pointAtCenter: false }}
      content={<CleaningPlanEventPopover hidePopover={handleHidePopover} timeSpan={label} event={event} />}
      destroyTooltipOnHide={true}
      open={visiblePopover}
      onOpenChange={handleVisibleChange}
      getPopupContainer={(triggerNode): HTMLElement =>
        (document.querySelector('.rbc-time-content') as HTMLElement) ?? triggerNode
      }
    >
      <StyledCleaningPlanEvent className="rbc-event cleaning-plan-event" style={eventStyle}>
        <p
          className={classNames('cleaning-plan-event__event-name', {
            'cleaning-plan-event__event-name--single-line': cleaningEventDurationMinutes <= 30,
          })}
        >
          {eventName}
        </p>
        <p className="cleaning-plan-event__time-span">{label}</p>
      </StyledCleaningPlanEvent>
    </Popover>
  );
};
